import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Model, Time } from "@models";
import { Helper } from "@helpers/functions";
import { debug } from "@helpers/debug";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";

// TODO: eslint fix
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
declare let Notification: Notification | any;

@Injectable({
	providedIn: "root"
})
export abstract class DesktopNotifyService<T extends Model> {
	protected abstract NOTIFICATION_ID: string;
	protected abstract translationService: TranslationService;
	protected abstract infoService: InfoService;
	protected abstract send(action: string, item: T): void;

	public constructor(public router: Router) {}

	protected isEnabled(): boolean {
		return Helper.getCookie("notification_" + this.NOTIFICATION_ID) === "true";
	}

	public show(action: string, object: T): void {
		if (this.isEnabled()) {
			this.send(action, object);
		}
	}

	protected desktopNotification(title: string, options: CustomNotificationOptions): void {
		// https://developer.mozilla.org/en-US/docs/Web/API/notification
		// https://caniuse.com/#feat=notifications
		if (!("Notification" in window)) {
			if (debug) {
				console.info("[NOTIF]", "This browser does not support desktop notification.");
			}
		} else if (Notification.permission === "granted") {
			this.sendDesktopNotification(title, options);
		} else if (Notification.permission !== "denied" || Notification.permission === "default") {
			this.requestPermission().then((result) => {
				if (this.checkPermission(result)) {
					this.sendDesktopNotification(title, options);
				}
			});
		}
	}

	protected desktopNotificationPrice(title: string, options: CustomNotificationOptions): void {
		if (!("Notification" in window)) {
			if (debug) {
				console.info("[NOTIF]", "This browser does not support desktop notification.");
			}
		} else if (Notification.permission === "granted") {
			this.sendDesktopNotificationPrice(title, options);
		} else if (Notification.permission !== "denied" || Notification.permission === "default") {
			this.requestPermission().then((result) => {
				if (this.checkPermission(result)) {
					this.sendDesktopNotificationPrice(title, options);
				}
			});
		}
	}

	public sendDesktopNotificationPrice(title: string, options: CustomNotificationOptions): void {
		const notification = new Notification(title, {
			icon: "assets/favicons/android-chrome-192-192.png",
			body: options.body,
			tag: options.tag,
			timestamp: options.time.getUnix(),
			data: options.data
		});

		notification.onclick = () => {
			parent.focus();
			window.focus();
			notification.close();

			if (window.location.href.includes(options.url)) {
				return;
			}
			this.router.navigateByUrl(options.url);
		};
	}

	private sendDesktopNotification(title: string, options: CustomNotificationOptions): void {
		// "tag" handles multi tab scenario i.e when multiple tabs are
		// Open then only one notification is sent
		const notification = new Notification(title, {
			icon: "assets/favicons/android-chrome-192-192.png",
			body: options.body,
			tag: options.tag,
			timestamp: options.time.getUnix()
		});

		notification.onclick = function (): void {
			parent.focus();
			window.focus(); // Just in case, older browsers
			this.close();
		};
	}

	private checkPermission(result: string): boolean {
		switch (result) {
			case "denied": {
				if (debug) {
					console.info("[NOTIF]", "Permission wasn't granted. Allow a retry.");
				}
				return false;
			}
			case "default": {
				if (debug) {
					console.info("[NOTIF]", "The permission request was dismissed.");
				}
				return false;
			}
			default: {
				return true;
			}
		}
	}

	private requestPermission(): Promise<NotificationPermission> {
		return Notification.requestPermission();
	}

	public checkIfEnabled(): void {
		if (!("Notification" in window)) {
			if (debug) {
				console.info("[NOTIF]", "This browser does not support desktop notification.");
			}
		} else if (Notification.permission === "denied" || Notification.permission === "default") {
			this.infoService.setWarning(this.translationService.translate("notification_enable_it"));
			this.requestPermission();
		}
	}
}

interface CustomNotificationOptions extends NotificationOptions {
	time?: Time;
	url?: string;
}
